import * as React from 'react'
import * as Tooltip from 'react-tooltip'
import { browserHistory, InjectedRouter, PlainRoute, RouteComponentProps } from 'react-router'
import { inject, observer } from 'mobx-react'
import { message, Button } from 'antd'
import {
  ILocation,
  API,
  productName,
  IBaseProduct,
  ITransfer,
  CreateOrUpdateTransfer,
  TransferStatus,
  PermissionNames,
  IInventory,
  TransferProduct,
} from '@getgreenline/homi-shared'
import { InventoryLogAPI } from '@getgreenline/inventory'
import { IInventoryLog } from '@getgreenline/inventory/build/models/inventoryLog'
import { LoadingAnimation } from '../../../../../components/LoadingAnimation'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { TransferStore } from '../TransferStore'
import { AppMainView, AppView } from '../../../../../components/AppView/index'
import AppHeader from '../../../../../components/AppView/Header'
import { Routes } from '../../../../../constants/Routes'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { TransferRow } from './TransferRow'
import { parseAndDownloadCSV, parseErrorMsg } from '../../../../../utilities/helpers'
import { BarcodeListener } from '../../../../../components/BarcodeListener'
import { TransferInventoryLogTable } from '../../../../../components/InventoryLogs/TransferInventoryLogTable'
import { SellableProductAsyncInput } from '../../../../../components/ProductAsyncInput/SellableProductAsyncInput'
import { EditTransferMetaTable } from './EditTransferMetaTable'
import { TooltipWithIcon } from '../../../../../components/TooltipWithIcon'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import Helmet from 'react-helmet'
import { ImportTransferModal } from './ImportTransferModal'
import { Track } from '../../../../../Track'
import { chunk, isEmpty } from 'lodash'
import { NoPermissionsResult } from '../../../../../components/NoPermissionsResult'

interface Params {
  transferId?: string
}

interface Props extends RouteComponentProps<Params, {}> {
  router: InjectedRouter
  route: PlainRoute
  currentCompanyStore: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

interface State {
  isSaving: boolean
  inventoryLogs?: IInventoryLog[]
  savedTransferStatus?: TransferStatus
  showImportModal: boolean
  showInventoryLogs: boolean
  isLoadingInventoryLogs: boolean
  isLoadingInventory: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class EditTransfer extends React.Component<Props, State> {
  transferStore = new TransferStore()

  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      isSaving: false,
      savedTransferStatus: undefined,
      showImportModal: false,
      showInventoryLogs: false,
      isLoadingInventoryLogs: false,
      isLoadingInventory: false,
    }
  }

  componentDidMount = async () => {
    const { transferId } = this.props.params
    if (transferId) {
      this.loadTransfer(transferId)
    }

    // Get ajax calls for the latest dropdown field data
    this.props.currentCompanyStore!.getLocations()
    this.props.currentCompanyStore!.productStore!.getCategories()

    this.setTransferFromLocation()

    this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave)
  }

  routerWillLeave = () => {
    if (this.transferStore.hasUnsavedChanges) {
      return 'All unsaved changes will be discarded. Are you sure?'
    }
  }

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    if (this.props.params.transferId !== newProps.params.transferId) {
      this.transferStore.clear()
      if (newProps.params.transferId) {
        this.loadTransfer(newProps.params.transferId)
      }
    }
  }

  setTransferFromLocation = () => {
    const { locations } = this.props.currentCompanyStore!
    if (locations && locations[0]) {
      this.transferStore.editTransfer.setFromLocation(locations[0].id)
    }
  }

  getInventoryLogs = async (transferId: string) => {
    try {
      const logs = await InventoryLogAPI.getInventoryLogs(this.company.id, {
        limit: 200,
        offset: 0,
        transferId,
      })
      this.setState({ inventoryLogs: logs.data })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while retrieving inventory logs')
    }
  }

  loadTransfer(transferId: string) {
    this.props.currentCompanyStore!.getTransferById(transferId).then((transfer) => {
      this.transferStore.populate(transfer)

      this.setSavedTransferStatus()
    })
  }

  setSavedTransferStatus = () =>
    this.setState({ savedTransferStatus: this.transferStore.editTransfer.status })

  async checkBarcode(barcode: string) {
    const flatProducts = await API.getFlatProducts(this.company.id, {
      barcode: barcode,
    })

    if (flatProducts.length === 0) {
      return
    }

    const matchingProduct = flatProducts[0]

    // If product exists, increment
    const transferProductLine = this.transferStore.editTransfer.products.find(
      (p) => p.id === matchingProduct.id,
    )

    if (transferProductLine) {
      return transferProductLine.setQuantity((transferProductLine.quantity || 0) + 1)
    }

    // Otherwise, add
    this.transferStore.editTransfer.addProduct({
      id: matchingProduct.id,
      name: productName(matchingProduct),
      sku: matchingProduct.sku,
      categoryName: matchingProduct.categoryName,
      categoryId: matchingProduct.categoryId,
      unit: matchingProduct.unit,
      quantity: 1,
      notes: null,
      indexOrder: this.transferStore.editTransfer.products.length,
      total: matchingProduct.purchasePrice || 0,
      isBatchTracked: matchingProduct.isBatchTracked,
    })
  }
  printLabels() {
    const transfer = this.transferStore.editTransfer
    const configString = JSON.stringify({
      products: transfer.products.map((product) => {
        return {
          productId: product.id,
          quantity: product.quantity || 1,
        }
      }),
    })
    window.localStorage.setItem('printing-config', configString)
    window.open(Routes.PRINTING_CONFIG_VIEW(this.company.id, null), '_blank')
  }

  export() {
    const transfer = this.transferStore.editTransfer
    const locations: ILocation[] = this.props.currentCompanyStore!.locations || []

    const productJSON = transfer.products.map((product) => {
      let batchString = ''

      if (product.isBatchTracked) {
        batchString = product.batches.reduce((prev, curr) => {
          if (curr.quantity === undefined) {
            return ''
          }
          return (
            prev +
            `${curr.batchNumber}: ${curr.quantity} ${
              curr.quantity > 1 ? product.unit + 's' : product.unit
            }`
          )
        }, batchString)
      }
      const source = locations.find(
        (location: ILocation) => location.id === transfer.fromLocationId,
      )?.name

      const dest = locations.find(
        (location: ILocation) => location.id === transfer.toLocationId,
      )?.name

      return {
        ID: product.id,
        Product: product.name,
        SKU: product.sku,
        Category: product.categoryName,
        Source: source,
        'Source stock': product.fromLocationInventory,
        Dest: dest,
        'Dest stock': product.toLocationInventory,
        Quantity: product.quantity,
        'Cost/unit': product.costPerUnit / 100,
        'Total cost': product.total / 100,
        Notes: product.notes,
        Batches: batchString,
      }
    })

    parseAndDownloadCSV(`transfer-${transfer.id}-${transfer.status}`, productJSON)
  }

  editTransfer = async (existingTransfer: ITransfer, editTransfer: CreateOrUpdateTransfer) => {
    try {
      const transfer = await this.props.currentCompanyStore!.updateTransfer(
        existingTransfer.id,
        editTransfer,
      )

      Track.track('dashboard:transfer_updated', transfer)
      this.transferStore.populate(transfer)
      this.transferStore.hasUnsavedChanges = false

      this.setState({ isSaving: false })

      message.success(`Updated`)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error updating your transfer')
      this.setState({ isSaving: false })
    }
  }

  createTransfer = async (createTransfer: CreateOrUpdateTransfer) => {
    try {
      const transfer = await this.props.currentCompanyStore!.createTransfer(createTransfer)

      Track.track('dashboard:transfers_created', transfer)
      message.success(`Added new transfer`)
      browserHistory.push(Routes.DASHBOARD_INVENTORY_BULK_TRANSFER(this.company.id))
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error creating your transfer')
      this.setState({ isSaving: false })
    }
  }

  save = async () => {
    const existingTransfer = this.transferStore.existingTransfer
    const editTransfer = this.transferStore.editTransfer
    editTransfer.products.forEach((p, index) => {
      p.quantity = p.quantity || 0
      p.indexOrder = index
    })

    if (!existingTransfer) {
      // Transfer is being made
      if (editTransfer.status === 'inProgress') {
        if (
          !window.confirm(
            'Are you sure? Your inventory will be subtracted from your source location.',
          )
        ) {
          return
        }
      }
      if (editTransfer.status === 'completed') {
        if (
          !window.confirm(
            'Are you sure? Your inventory will be subtracted from your source location and added to your desination location.',
          )
        ) {
          return
        }
      }
    }

    if (
      existingTransfer &&
      existingTransfer.status !== 'completed' &&
      editTransfer.status === 'completed'
    ) {
      if (
        !window.confirm(
          'Are you sure? Your inventory will be checked in after completing a transfer.',
        )
      ) {
        return
      }
    }

    if (editTransfer.status === 'cancelled') {
      if (!window.confirm('Are you sure?')) {
        // Proceed
        return
      }
    }

    this.setState({ isSaving: true })

    if (existingTransfer) {
      await this.editTransfer(existingTransfer, editTransfer)
    } else {
      await this.createTransfer(editTransfer)
    }

    this.setSavedTransferStatus()

    if (this.props.params.transferId) {
      await this.getInventoryLogs(this.props.params.transferId)
    }
  }

  onProductSelect = async (product: IBaseProduct) => {
    const name = productName(product)

    this.transferStore.editTransfer.addProduct({
      id: product.id,
      name: name,
      sku: product.sku,
      categoryName: product.categoryName,
      categoryId: product.categoryId,
      unit: product.unit,
      quantity: 1,
      notes: null,
      indexOrder: this.transferStore.editTransfer.products.length,
      total: 0,
      isBatchTracked: product.isBatchTracked,
    })

    const costPerUnit = await API.checkCostPerUnit(this.company.id, product.id)

    const lastIndex = this.transferStore.editTransfer.products.length - 1
    const lastTransferProduct = this.transferStore.editTransfer.products[lastIndex]
    if (lastTransferProduct.id === product.id && costPerUnit) {
      lastTransferProduct.setCostPerUnit(costPerUnit)
    }

    this.transferStore.hasUnsavedChanges = true
    message.success(`${name} added`)
  }

  eligibleTransferProduct = (product: IBaseProduct) => {
    const existingProduct = this.transferStore.editTransfer.products
      .map((transferProduct) => transferProduct.id)
      .includes(product.id)
    return !existingProduct
  }

  get batchTrackedProductExists(): boolean {
    return this.transferStore.editTransfer.products.some((product) => product.isBatchTracked)
  }

  get productsWithBatchOptionsExist(): boolean {
    return this.transferStore.editTransfer.products.some(
      (product) => product.batchOptions.length > 0,
    )
  }

  get shouldLotColumnExist(): boolean {
    return this.batchTrackedProductExists && this.productsWithBatchOptionsExist
  }

  get saveButton(): JSX.Element {
    const transfer = this.transferStore.editTransfer
    const canEditTransfers = this.props.currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_TRANSFERS,
      this.company.id,
    )

    let buttonTitle: string
    let disabled = true
    const style = { minWidth: 200 }

    if (!transfer.fromLocationId) {
      buttonTitle = 'Source location required'
    } else if (!transfer.toLocationId) {
      buttonTitle = 'Destination required'
    } else if (!transfer.title) {
      buttonTitle = 'Title required'
    } else if (transfer.products.length === 0 && transfer.status !== 'cancelled') {
      buttonTitle = 'Please add at least 1 product'
    } else if (this.state.isSaving) {
      buttonTitle = 'Saving...'
    } else if (this.transferStore.hasUnsavedChanges) {
      buttonTitle = 'Save (unsaved changes)'
      disabled = false
    } else {
      buttonTitle = 'Save'
      disabled = !canEditTransfers
    }

    return (
      <BlazeButton
        buttonProps={{
          type: 'primary',
          onClick: () => this.save(),
          style,
          disabled,
        }}
        titleProps={{
          title: buttonTitle,
        }}
      />
    )
  }

  getProductIds(products: TransferProduct[]): string[] {
    return products.map((product) => product.id).filter((id): id is string => Boolean(id))
  }

  async getInventoryForLocation(
    locationId: number | undefined,
    productIds: string[],
  ): Promise<IInventory[]> {
    if (!locationId) {
      return []
    }

    return API.getInventory(this.company.id, locationId, { productIds })
  }

  mapInventoryByProductId(inventoryList: IInventory[]): Map<string, IInventory> {
    return new Map<string, IInventory>(
      inventoryList.map((inventory) => [inventory.product.id, inventory]),
    )
  }

  updateProductInventory(
    products: TransferProduct[],
    fromLocationInventoryMap: Map<string, IInventory>,
    toLocationInventoryMap: Map<string, IInventory>,
  ) {
    products.forEach((product) => {
      if (!product.id) {
        return
      }

      const fromLocationInventory = fromLocationInventoryMap.get(product.id)
      const toLocationInventory = toLocationInventoryMap.get(product.id)

      if (fromLocationInventory) {
        product.fromLocationInventory = fromLocationInventory.quantity
      }

      if (toLocationInventory) {
        product.toLocationInventory = toLocationInventory.quantity
      }
    })
  }

  async loadAllExistingInventory() {
    this.setState({ isLoadingInventory: true })

    const { products, fromLocationId, toLocationId } = this.transferStore.editTransfer

    const batchSize = 150
    const batches = chunk(products, batchSize)

    for (const batchProducts of batches) {
      const batchProductIds = this.getProductIds(batchProducts)

      const [fromBatchInventory, toBatchInventory] = await Promise.all([
        this.getInventoryForLocation(fromLocationId, batchProductIds),
        this.getInventoryForLocation(toLocationId, batchProductIds),
      ])

      const mappedFromLocationInventory = this.mapInventoryByProductId(fromBatchInventory)
      const mappedToLocationInventory = this.mapInventoryByProductId(toBatchInventory)

      this.updateProductInventory(
        batchProducts,
        mappedFromLocationInventory,
        mappedToLocationInventory,
      )
    }

    this.setState({ isLoadingInventory: false })
  }

  render() {
    const transfer = this.transferStore.editTransfer
    const categories = this.props.currentCompanyStore!.productStore!.flattenedCategories || []

    const userCanViewTransfers = this.props.currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_TRANSFERS,
      this.company.id,
    )

    if (!userCanViewTransfers) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_TRANSFERS} />
    }

    if (this.props.params.transferId && !this.transferStore.editTransfer.id) {
      return <LoadingAnimation />
    }

    const canEditTransfers = this.props.currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_TRANSFERS,
      this.company.id,
    )

    return (
      <AppView column>
        <Helmet>
          <title>Inventory | Transfers | Edit</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <AppHeader
          title={`${this.transferStore.editTransfer.title}`}
          backVisible={true}
          onBackClick={() =>
            browserHistory.push({
              pathname: Routes.DASHBOARD_INVENTORY_BULK_TRANSFER(this.company.id),
              query: browserHistory.getCurrentLocation().query,
            })
          }
        >
          <span className='spaced-button-wrapper'>
            <BlazeButton
              buttonProps={{
                icon: 'barcode',
                onClick: () => this.printLabels(),
              }}
              titleProps={{
                title: 'Print barcodes',
                widthBreakPoint: 2560,
              }}
            />
            {!!transfer.id && (
              <BlazeButton
                buttonProps={{
                  icon: 'download',
                  onClick: () => this.export(),
                }}
                titleProps={{
                  title: 'Export',
                  widthBreakPoint: 2560,
                }}
              />
            )}
            <BlazeButton
              buttonProps={{
                icon: 'upload',
                onClick: () => this.setState({ showImportModal: true }),
                disabled: !(this.transferStore.isEditable && canEditTransfers),
              }}
              titleProps={{
                title: 'Import',
              }}
            />
            {this.saveButton}
          </span>
        </AppHeader>

        <AppMainView>
          <iframe
            id='printing-frame'
            name='print_frame'
            src='about:blank'
            style={{ display: 'none' }}
          ></iframe>

          <div className='intercom-pb-2'>
            <div className='mt-2 pt-4 pb-1'>
              <div className='container'>
                <div className='table table-responsive mb-0'>
                  <EditTransferMetaTable
                    transfer={transfer}
                    transferStore={this.transferStore}
                    savedTransferStatus={this.state.savedTransferStatus}
                  />
                </div>
              </div>
            </div>

            <div className='container'>
              <div className='d-flex' style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                {this.transferStore.isEditable && canEditTransfers && (
                  <>
                    <div className='mr-2' style={{ width: 300 }}>
                      <SellableProductAsyncInput
                        isInventoryProducts
                        optionFilterFunction={(unFilteredProducts) =>
                          unFilteredProducts.filter(this.eligibleTransferProduct)
                        }
                        onSelect={this.onProductSelect}
                        placeholder='Add product'
                      />
                    </div>
                  </>
                )}

                {this.transferStore.isEditable && canEditTransfers && (
                  <BlazeButton
                    buttonProps={{
                      className: 'mr-2',
                      type: 'danger',
                      size: 'large',
                      onClick: () => {
                        if (window.confirm('Are you sure?')) {
                          transfer.products = []
                          this.transferStore.hasUnsavedChanges = true
                        }
                      },
                    }}
                    titleProps={{
                      title: 'Clear all',
                    }}
                  />
                )}
                <BlazeButton
                  buttonProps={{
                    className: 'ml-2',
                    type: 'default',
                    size: 'large',
                    onClick: () => {
                      this.loadAllExistingInventory()
                    },
                    disabled: isEmpty(this.transferStore.editTransfer.products),
                    loading: this.state.isLoadingInventory,
                  }}
                  titleProps={{
                    title: 'Show all stock',
                  }}
                />
              </div>
            </div>

            <br />

            <div className='container'>
              <table className='table table-sm'>
                <thead className='thead-light'>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Source stock </th>
                    <th>Dest stock</th>
                    <th>Quantity</th>
                    {this.shouldLotColumnExist && (
                      <th>
                        <span className='mr-2'>Lot #</span>
                        <TooltipWithIcon
                          tooltipIconClassName='fa fa-info-circle text-muted'
                          tooltipId='transfer-product-lots'
                          tooltipContent={
                            <div className='text-left'>
                              <div>
                                Cannabis products come with batch/lot numbers on the packaging.
                              </div>
                              <div>Record the numbers here for return/recall management.</div>
                              <div>
                                If there are multiple lots in a shipment, separate them using
                                commas. Example: 234423, 405930
                              </div>
                            </div>
                          }
                        />
                      </th>
                    )}
                    <th>
                      <span className='mr-2'>Cost/unit</span>
                      <span
                        className='fa fa-info-circle text-muted'
                        data-tip
                        data-for='cost-per-unit'
                      />
                      <Tooltip id='cost-per-unit' place='right' effect='solid'>
                        <div style={{ fontWeight: 'normal' }}>
                          This cost will be automatically set by your latest purchase price.
                        </div>
                      </Tooltip>
                    </th>
                    <th>Total cost</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {this.transferStore.editTransfer.products.map((product, index) => {
                    return (
                      <TransferRow
                        key={index}
                        transferProduct={product}
                        transferStore={this.transferStore}
                        canEdit={canEditTransfers}
                        shouldLotColumnExist={this.shouldLotColumnExist}
                        index={index}
                      />
                    )
                  })}
                </tbody>
              </table>

              <div className='row mt-5'>
                <div className='container'>
                  <div>
                    <h6 className='font-weight-bold text-dark d-inline mr-2'>Inventory logs</h6>
                    <Button
                      loading={this.state.isLoadingInventoryLogs}
                      onClick={async () => {
                        if (!this.state.showInventoryLogs) {
                          this.setState({ isLoadingInventoryLogs: true })
                          if (
                            this.state.inventoryLogs === undefined &&
                            this.props.params.transferId
                          ) {
                            await this.getInventoryLogs(this.props.params.transferId)
                          }
                          this.setState({ isLoadingInventoryLogs: false })
                        }
                        this.setState({ showInventoryLogs: !this.state.showInventoryLogs })
                      }}
                    >
                      {this.state.showInventoryLogs ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                  {this.state.showInventoryLogs && (
                    <TransferInventoryLogTable
                      inventoryLogs={this.state.inventoryLogs || []}
                      companyId={this.company.id}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </AppMainView>

        {this.state.showImportModal && (
          <ImportTransferModal
            transferId={this.props.params.transferId}
            onClose={() => this.setState({ showImportModal: false })}
            onSuccess={(importedProducts) => {
              this.setState({ showImportModal: false })
              importedProducts.forEach((product) => {
                this.transferStore.editTransfer.addProduct(product)
              })
              this.transferStore.hasUnsavedChanges = true
              message.success(`CSV successfully imported`)
            }}
          />
        )}

        <BarcodeListener onBarcodeEnter={(barcode) => this.checkBarcode(barcode)} />
      </AppView>
    )
  }
}
